import clsx from "clsx";
import { useQuery } from "@tanstack/react-query";
import {
  fetchAdsOptimizationConfigChanges,
  type AdsOptimizationConfigChangeAction,
  type AdsOptimizationConfigChangeDto,
} from "../../../helpers/api/ads-optimization";

interface Props {
  customerId: string;
}

// Two formats. The row header timestamp needs the time (admins want to know
// when a config change happened to the minute). dryRunUntil only needs the
// date because the agent's window is a calendar concept, not a clock one.
// Same-day re-click of "Start dry-run" will now silently dedupe because the
// formatted before/after values are equal.
const FMT_DATETIME = new Intl.DateTimeFormat("en-US", {
  dateStyle: "long",
  timeStyle: "short",
});
const FMT_DATE_ONLY = new Intl.DateTimeFormat("en-US", {
  dateStyle: "long",
});
function fmtDateTime(iso: string | Date): string {
  return FMT_DATETIME.format(typeof iso === "string" ? new Date(iso) : iso);
}
function fmtDateOnly(iso: string | Date): string {
  return FMT_DATE_ONLY.format(typeof iso === "string" ? new Date(iso) : iso);
}

const ACTION_LABELS: Record<AdsOptimizationConfigChangeAction, string> = {
  start_dry_run: "Started dry-run",
  switch_to_live: "Switched to live",
  pause: "Paused (kill switch on)",
  resume: "Resumed (kill switch off)",
  turn_off: "Turned off",
  enable: "Enabled",
  update_settings: "Updated settings",
};

const ACTION_STYLES: Record<AdsOptimizationConfigChangeAction, string> = {
  start_dry_run: "bg-blue-50 text-blue-700",
  switch_to_live: "bg-emerald-50 text-emerald-700",
  pause: "bg-rose-50 text-rose-700",
  resume: "bg-amber-50 text-amber-700",
  turn_off: "bg-gray-100 text-gray-700",
  enable: "bg-emerald-50 text-emerald-700",
  update_settings: "bg-gray-100 text-gray-700",
};

// Diff-friendly fields, in display order. `enabled` and `killSwitch` are
// intentionally NOT in this list because the action badge above already
// conveys those (Enabled, Turned off, Paused, Resumed). Repeating them as
// "Enabled: No → Yes" was noisy.
const FIELDS: Array<{ key: string; label: string; format?: (v: unknown) => string }> = [
  {
    key: "dryRunUntil",
    label: "Dry-run until",
    format: (v) => (v ? fmtDateOnly(v as string) : "(none)"),
  },
  { key: "dailyBudgetCap", label: "Daily budget cap", format: (v) => `$${v}` },
  { key: "notOnOtas", label: "Not on OTAs" },
  { key: "bpComplaints", label: "BP complaints" },
  { key: "allowBpDominance", label: "Allow BP dominance" },
  { key: "excludeFromReallocation", label: "Exclude from reallocation" },
];

function formatValue(v: unknown, format?: (v: unknown) => string): string {
  if (format) return format(v);
  if (v === null || v === undefined) return "(none)";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

function changedFields(row: AdsOptimizationConfigChangeDto) {
  const before = row.before ?? {};
  const after = row.after ?? {};
  return FIELDS.filter((f) => {
    const b = formatValue(before[f.key], f.format);
    const a = formatValue(after[f.key], f.format);
    return b !== a;
  }).map((f) => ({
    label: f.label,
    before: formatValue(before[f.key], f.format),
    after: formatValue(after[f.key], f.format),
  }));
}

export function AdsOptimizationConfigHistory({ customerId }: Props) {
  const { data, isLoading } = useQuery<AdsOptimizationConfigChangeDto[]>({
    queryKey: ["ads-optimization", "config-changes", customerId],
    queryFn: () => fetchAdsOptimizationConfigChanges(customerId, { limit: 50 }),
  });

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm flex flex-col">
      <header className="px-4 py-3 border-b border-border-secondary">
        <h2 className="text-sm font-semibold text-content-primary">
          Configuration history
        </h2>
        <p className="text-[11px] text-content-tertiary leading-snug mt-0.5">
          Every change made to this agent's configuration (who, when, what
          changed). Records what humans did. The decision log above records
          what the agent did.
        </p>
      </header>

      {isLoading ? (
        <div className="p-4 h-24 bg-gray-50 animate-pulse" />
      ) : !data || data.length === 0 ? (
        <div className="p-6 text-center text-xs text-content-tertiary">
          No configuration changes yet.
        </div>
      ) : (
        <ol className="divide-y divide-border-secondary">
          {data.map((row) => {
            const fields = changedFields(row);
            return (
              <li key={row.id} className="px-4 py-3 flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span
                      className={clsx(
                        "px-1.5 py-0.5 rounded text-[10px] font-medium",
                        ACTION_STYLES[row.action],
                      )}
                    >
                      {ACTION_LABELS[row.action] ?? row.action}
                    </span>
                    <span className="text-xs text-content-secondary">
                      {row.changedByEmail ?? row.changedByUserId ?? "system"}
                    </span>
                  </div>
                  <span className="text-[11px] text-content-tertiary whitespace-nowrap">
                    {fmtDateTime(row.createdAt)}
                  </span>
                </div>
                {fields.length > 0 && (
                  <ul className="text-[11px] text-content-secondary leading-snug pl-1 flex flex-col gap-0.5">
                    {fields.map((f) => (
                      <li key={f.label}>
                        <span className="text-content-tertiary">
                          {f.label}:
                        </span>{" "}
                        <span className="font-mono">{f.before}</span>
                        <span className="text-content-tertiary"> → </span>
                        <span className="font-mono font-medium">
                          {f.after}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
